/**
 * Custom hook for handling file uploads with TanStack Query
 * Provides upload functionality with proper error handling and progress tracking
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  uploadFiles,
  UploadRequest,
  UploadResponse,
  uploadMutationOptions
} from '../api/uploads';
import { UploadApiError } from '../api/base/errors';
import { conversationQueryKeys } from '../api/conversations';
import { showNotification } from '../components/Notification';
import {
  ACCEPTED_FILE_TYPE_LABEL,
  MAX_FILES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  validateFileTypes,
} from '../features/chat/utils/fileUploadValidation';
import { useChatStore } from '../stores/chatStore';

/**
 * Upload hook options for customizing behavior
 */
export interface UseFileUploadOptions {
  onUploadStart?: (files: File[], deletedFiles?: string[]) => void;
  onUploadSuccess?: (response: UploadResponse) => void;
  onUploadError?: (error: UploadApiError) => void;
  onComplete?: () => void;
}

/**
 * Upload hook return interface
 */
export interface UseFileUploadReturn {
  uploadFiles: (request: UploadRequest) => Promise<UploadResponse>;
  isUploading: boolean;
  uploadError: UploadApiError | null;
  reset: () => void;
}

/**
 * Custom hook for file upload operations
 * Integrates with TanStack Query for proper state management
 */
export function useFileUpload(
  conversationId: string,
  options: UseFileUploadOptions = {}
): UseFileUploadReturn {
  const queryClient = useQueryClient();
  const {
    onUploadStart,
    onUploadSuccess,
    onUploadError,
    onComplete
  } = options;

  // Create the upload mutation
  const uploadMutation = useMutation({
    ...uploadMutationOptions(conversationId),
    onMutate: async (variables: UploadRequest) => {
      // Call onUploadStart callback with both files and deleted files
      onUploadStart?.(variables.files, variables.deletedFiles);

      // Discard any pre-operation conversation response. The mutation's pending
      // state provides immediate UI feedback; processing polling starts only
      // after the backend acknowledgement in onSuccess.
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.detail(conversationId),
      });

      return {
        uploadStartTime: Date.now(),
        fileCount: variables.files.length,
        previousConversationData: undefined,
      };
    },
    onSuccess: (response: UploadResponse, variables) => {
      // Backend returned 202 — files accepted, processing in background
      // Set processing status so UI blocks chat input until WS/poll notification
      if (response.status === 'processing') {
        useChatStore.getState().setFileProcessingStatus(
          response.conversation_id || variables.conversationId,
          'processing',
        );
      }

      // Success callback
      onUploadSuccess?.(response);

      // Determine if this is a batch operation (both upload and delete)
      const isBatchOperation = (variables.deletedFiles?.length ?? 0) > 0 && variables.files.length > 0;

      if (isBatchOperation) {
        // For batch operations, preserve optimistic updates to avoid UI inconsistencies
        // Cache will be invalidated later when user sends next message
      } else {
        // For single operations (upload-only or delete-only), safe to fetch fresh data immediately
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys.detail(conversationId),
        });
      }

      // Complete callback
      onComplete?.();
      showNotification('Your files are being processed');
    },
    onError: (error: UploadApiError, variables, context) => {
      console.error('Batch operation failed:', {
        error: error.message,
        status: error.status,
        uploadDetails: error.uploadDetails,
        filesUploaded: variables.files.length,
        filesDeleted: variables.deletedFiles?.length || 0,
        context,
      });

      // Clear processing banner on error when deletions were involved
      if (variables.deletedFiles && variables.deletedFiles.length > 0) {
        useChatStore.getState().clearFileProcessingStatus(conversationId);
        variables.deletedFiles.forEach((filename) => {
          useChatStore.getState().markFileUploaded(conversationId, filename);
        });
      }

      // Error callback
      onUploadError?.(error);

      // Complete callback (even on error)
      onComplete?.();
    },
    onSettled: () => {
      // Always called after success or error
    }
  });

  // Wrapper function for easier usage
  const upload = useCallback(async (request: UploadRequest): Promise<UploadResponse> => {
    // Basic validation
    if (!request.files.length) {
      throw new UploadApiError('No files selected for upload', 400);
    }

    if (!request.conversationId) {
      throw new UploadApiError('Conversation ID is required', 400);
    }

    for (const file of request.files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new UploadApiError(
          `File "${file.name}" is too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum size is ${MAX_FILE_SIZE_LABEL}.`,
          413
        );
      }
    }

    const fileTypeValidation = validateFileTypes(request.files);
    if (!fileTypeValidation.valid) {
      throw new UploadApiError(
        fileTypeValidation.message || `Unsupported file type. Supported types: ${ACCEPTED_FILE_TYPE_LABEL}`,
        415
      );
    }

    // Check total file count
    if (request.files.length > MAX_FILES) {
      throw new UploadApiError(
        `Too many files selected (${request.files.length}). Maximum is ${MAX_FILES} files.`,
        400
      );
    }

    return uploadMutation.mutateAsync(request);
  }, [uploadMutation]);

  return {
    uploadFiles: upload,
    isUploading: uploadMutation.isPending,
    uploadError: uploadMutation.error,
    reset: uploadMutation.reset,
  };
}
